import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Loader2, MessageCircle, MessageSquare, Eye, Ruler } from "lucide-react";
import { api, fmt } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { lp, catalogueLabel, catalogueShortLabel, catalogueFamily, FAMILY_NAMES, SPORTS, productAlt } from "../lib/localize";
import ProductPopup from "../components/ProductPopup";
import ImageLightbox from "../components/ImageLightbox";
import { FadeUp } from "../components/motion";
import RichText from "../components/RichText";
import { ELink } from "../components/Editable";
import { SEO } from "../components/SEO";
import { CallRequestDialog } from "../components/CallRequestDialog";
import { useShopSettings, isSectionOff } from "../lib/shopSettings";
import SuggestionsRecherches from "../components/SuggestionsRecherches";
import { noterRecherche, noterCategorie } from "../lib/recherches";

const PAGE_SIZE = 48;

// La Boutique : grille de tous les produits (tous catalogues confondus).
// 1 produit par case — photo zoomable, description du catalogue d'origine,
// fiche rapide avec espace de personnalisation (texte + logo, comme Urbano).
export default function Catalogue() {
  const { lang, t, ts, tx } = useLang();
  const shopSettings = useShopSettings();
  const [facets, setFacets] = useState({ shop_categories: [], subcategories: [], colors: [], sizes: [], catalogues: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");  // ex. bannière d'accueil → /boutique?q=Plaque
  const [qDebounced, setQDebounced] = useState("");
  // Préfiltres par URL (ex. bannière d'accueil → /boutique?cat=Plaques&scat=...)
  const [shopCat, setShopCat] = useState(searchParams.get("cat") || "");   // grande catégorie : Trophées / Médailles / Plaques / Autres
  const [subCat, setSubCat] = useState(searchParams.get("scat") || "");    // sous-catégorie (Coupe Métal, Rubans, Bois de noyer…)
  const [color, setColor] = useState("");           // bande déroulante couleur
  const [size, setSize] = useState("");             // bande déroulante grandeur
  const [catNo, setCatNo] = useState(searchParams.get("cn") || "");  // catalogue fournisseur (ex. /boutique?cn=3.0)
  const [sport, setSport] = useState("");           // filtre par sport (tous catalogues confondus)
  const [sort, setSort] = useState("price_asc");    // tri : prix croissant par défaut
  const [popupPid, setPopupPid] = useState(null);
  const [zoomImg, setZoomImg] = useState(null);  // {src, alt}
  const seq = useRef(0);  // ignore les réponses de recherche périmées

  // changer de grande catégorie réinitialise les sous-filtres
  const pickShopCat = (v) => {
    setShopCat(v); setSubCat(""); setColor(""); setSize("");
    if (v) noterCategorie(v);  // témoin des catégories visitées (suggestions à l'arrivée)
  };

  // Recherche avec petit délai (évite un appel par touche)
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(query), 350);
    return () => clearTimeout(id);
  }, [query]);

  const fetchPage = useCallback(async (offset, append) => {
    const mySeq = ++seq.current;
    const params = { limit: PAGE_SIZE, offset };
    if (qDebounced) params.q = qDebounced;
    if (catNo) params.catalogue_no = catNo;
    if (shopCat) params.shop_category = shopCat;
    if (subCat) params.shop_subcategory = subCat;
    if (color) params.color = color;
    if (size) params.size = size;
    if (sport) params.sport = sport;
    if (sort) params.sort = sort;
    try {
      const { data } = await api.get("/products/browse", { params });
      if (mySeq !== seq.current) return;  // une recherche plus récente a répondu
      setTotal(data.total);
      const parNom = (a, b) => String(a.name ?? a).localeCompare(String(b.name ?? b), "fr");
      setFacets({
        shop_categories: data.shop_categories || [],  // ordre d'affaires conservé (Trophées, Médailles…)
        // Listes en ORDRE ALPHABÉTIQUE (demande du propriétaire) — sauf les grandeurs,
        // qui gardent leur ordre naturel (petit → grand)
        subcategories: [...(data.subcategories || [])].sort(parNom),
        colors: [...(data.colors || [])].sort(parNom),
        sizes: data.sizes || [],
        catalogues: [...(data.catalogues || [])].sort(parNom),
      });
      setItems((prev) => (append ? [...prev, ...data.products] : data.products));
      // Témoin des recherches (demande du propriétaire, 2026-10-16) : la recherche
      // aboutie est mémorisée chez le visiteur pour lui suggérer de meilleurs
      // résultats à sa prochaine arrivée — rien n'est envoyé au serveur
      if (!append && qDebounced) noterRecherche(qDebounced);
    } catch {
      if (mySeq === seq.current && !append) { setItems([]); setTotal(0); }
    }
  }, [qDebounced, catNo, shopCat, subCat, color, size, sport, sort]);

  useEffect(() => {
    setLoading(true);
    fetchPage(0, false).finally(() => setLoading(false));
  }, [fetchPage]);

  const showMore = async () => {
    setLoadingMore(true);
    await fetchPage(items.length, true);
    setLoadingMore(false);
  };

  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const pillCls = (on) => `px-4 py-1.5 rounded-full text-xs border transition-colors duration-300 ${on ? "border-gold bg-gold text-ink font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/50 hover:text-gold"}`;
  const selectCls = "max-w-full bg-surface border border-ivory/15 rounded-full px-4 py-2.5 text-sm text-ivory focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <div data-testid="boutique-page" className="pt-28 pb-16 min-h-screen overflow-x-hidden">
      <SEO title={ts("seo.catalogue_title")} description={ts("seo.catalogue_desc")} path="/boutique"
        image="/api/files/mrtrophee/products/tags/groupe-medailles-39.jpg"
        keywords={lang === "fr"
          ? "boutique trophées en ligne, acheter trophées personnalisés, médailles personnalisées pas chères, coupes sportives, plaques honorifiques gravées, prix de reconnaissance, récompenses d'entreprise Québec, trophées Montérégie, boutique récompenses Léry"
          : "online trophy shop, buy personalized trophies, custom medals, sports cups, engraved honour plaques, recognition awards, corporate awards Quebec, trophies Monteregie, awards shop Lery"}
        jsonLd={[
          { "@context": "https://schema.org", "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: "https://mrtrophee.ca/" },
              { "@type": "ListItem", position: 2, name: lang === "fr" ? "La Boutique" : "Shop", item: "https://mrtrophee.ca/boutique" },
            ] },
          { "@context": "https://schema.org", "@type": "CollectionPage",
            name: ts("seo.catalogue_title"), description: ts("seo.catalogue_desc"),
            url: "https://mrtrophee.ca/boutique", inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
            mainEntity: { "@type": "ItemList", numberOfItems: total,
              itemListElement: items.slice(0, 30).map((p, i) => ({
                "@type": "ListItem", position: i + 1,
                url: `https://mrtrophee.ca/produit/${p.id}`, name: lp(p, lang).name,
              })) } },
        ]} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp className="mb-8">
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">
            {catNo ? catalogueLabel(catNo) : t("catalogue.kicker")}
          </p>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight mb-3">
            {t("catalogue.title_1")} <em className="italic text-gold">{t("catalogue.title_2")}</em>
          </h1>
          <p className="text-ivory/60 max-w-2xl">{t("catalogue.click_hint")}</p>
        </FadeUp>

        {/* Recherche — en haut de page, bien visible */}
        <FadeUp delay={0.05} className="mb-10">
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gold/70" />
            <input
              data-testid="catalogue-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={ts("catalogue.search_placeholder")}
              className="w-full bg-surface border border-gold/30 rounded-full pl-12 pr-4 py-4 text-base focus:border-gold focus:outline-none transition-colors duration-300"
            />
          </div>
        </FadeUp>

        {/* « Reprendre où vous étiez » — recherches et catégories récentes lues
            dans les témoins du visiteur (demande du propriétaire, 2026-10-16) ;
            visible seulement à l'arrivée, sans recherche ni filtre en cours */}
        {!qDebounced && !shopCat && !subCat && !catNo && !color && !size && !sport && (
          <SuggestionsRecherches lieu="boutique"
            onRecherche={(r) => setQuery(r)}
            onCategorie={(c) => pickShopCat(c)} />
        )}

        {/* Section « Médailles pour animaux » de la boutique (demande du
            propriétaire, 2026-10-16) — bande dédiée vers la page d'atterrissage
            SEO/GEO /boutique/animaux ; toujours à sa place, sans filtre en cours */}
        {!qDebounced && !shopCat && !subCat && !catNo && !color && !size && !sport && (
          <FadeUp>
            <Link to="/boutique/animaux" data-testid="boutique-animaux-band"
              className="group mb-8 flex flex-col sm:flex-row items-center gap-5 bg-surface/60 border border-gold/20 rounded-2xl p-5 hover:border-gold/50 transition-colors duration-300">
              <img src="/api/files/mrtrophee/products/tags/groupe-medailles-39.jpg"
                alt={t("home.pet_tags_title_1")} loading="lazy"
                className="w-full sm:w-44 h-36 object-cover rounded-xl" />
              <div className="flex-1 text-center sm:text-left">
                <p className="text-xs uppercase tracking-[0.25em] text-gold/80 mb-1">{t("home.pet_tags_kicker")}</p>
                <p className="font-serif text-2xl text-ivory group-hover:text-gold transition-colors">
                  {t("home.pet_tags_title_1")} {t("home.pet_tags_title_2")}
                </p>
                <p className="text-sm text-ivory/60 mt-1 max-w-xl">{t("home.pet_tags_desc")}</p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-2 bg-gold text-ink text-sm font-semibold px-5 py-2.5 rounded-full group-hover:bg-champagne transition-colors">
                {t("home.pet_tags_cta")}
              </span>
            </Link>
          </FadeUp>
        )}

        {/* Comment acheter + soumission rapide */}
        <FadeUp delay={0.08} className="mb-10">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface border border-ivory/10 rounded-xl p-6" data-testid="catalogue-howto">
              <h2 className="font-serif text-xl text-champagne mb-4">{t("catalogue.howto_title")}</h2>
              <ol className="space-y-2.5">
                {t("catalogue.howto_steps").map((s, i) => (
                  <li key={i} data-testid={`catalogue-howto-step-${i + 1}`} className="flex gap-3 text-sm text-ivory/70 leading-snug">
                    <span className="shrink-0 w-5 h-5 rounded-full border border-gold/50 text-gold text-[11px] flex items-center justify-center font-semibold">{i + 1}</span>
                    <RichText text={s} path={`catalogue.howto_steps.${i}`} />
                  </li>
                ))}
              </ol>
              <p className="mt-4 pt-4 border-t border-ivory/10 text-sm text-gold/90 leading-snug" data-testid="catalogue-howto-delays">
                {t("catalogue.howto_delays")}
              </p>
            </div>
            <div className="bg-surface border border-gold/25 rounded-xl p-6 spotlight flex flex-col justify-center" data-testid="catalogue-quote">
              <h2 className="font-serif text-xl text-gold mb-2">{t("catalogue.quote_title")}</h2>
              <p className="text-sm text-ivory/60 mb-5">{t("catalogue.quote_text")}</p>
              <div className="flex flex-wrap gap-3">
                <a href="https://m.me/trophee.ca" target="_blank" rel="noreferrer" data-testid="quote-messenger"
                  className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
                  <MessageCircle className="w-4 h-4" /> {t("catalogue.quote_messenger")}
                </a>
                <a href="sms:+15145656665" data-testid="quote-sms"
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
                  <MessageSquare className="w-4 h-4" /> {t("catalogue.quote_sms")}
                </a>
                <CallRequestDialog testid="quote-call" />
              </div>
            </div>
          </div>
        </FadeUp>

        {/* Grandes catégories : Trophées / Médailles / Plaques / Autres */}
        <FadeUp delay={0.1} className="mb-4">
          <div className="flex flex-wrap gap-2" data-testid="boutique-main-filters">
            <button onClick={() => pickShopCat("")} data-testid="boutique-shopcat-all" className={pillCls(!shopCat)}>
              {t("catalogue.grid_all")}
            </button>
            {facets.shop_categories.map((c) => (
              <button key={c.name} onClick={() => pickShopCat(shopCat === c.name ? "" : c.name)}
                data-testid={`boutique-shopcat-${slug(c.name)}`} className={pillCls(shopCat === c.name)}>
                {/* Nom traduit en /en (le FILTRE reste sur le nom français — stable) */}
                {(lang === "en" && c.name_en) || c.name} ({c.count})
              </button>
            ))}
          </div>
        </FadeUp>

        {/* Bandes déroulantes : sous-catégorie (LISTE DÉROULANTE — demande du
            propriétaire : le mur d'onglets était illisible), couleur, grandeur, catalogue */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="boutique-selects">
          {shopCat && facets.subcategories.length > 0 && (
            <select value={subCat} onChange={(e) => { setSubCat(e.target.value); setColor(""); setSize(""); }}
              data-testid="boutique-subcat-select" className={selectCls}>
              {/* label= plutôt qu'un enfant texte : évite l'avertissement d'hydratation
                  React (l'éditeur visuel injecte un <span> dans les <option>) */}
              <option value="" label={ts("catalogue.filter_subcat")} />
              {facets.subcategories.map((s) => (
                <option key={s.name} value={s.name} data-testid={`boutique-subcat-${slug(s.name)}`}
                  label={`${(lang === "en" && s.name_en) || s.name} (${s.count})`} />
              ))}
            </select>
          )}
          {facets.colors.length > 0 && (
            <select value={color} onChange={(e) => setColor(e.target.value)} data-testid="boutique-color-select" className={selectCls}>
              <option value="" label={ts("catalogue.filter_color")} />
              {facets.colors.map((c) => (
                <option key={c.name} value={c.name} data-testid={`boutique-color-${slug(c.name)}`}
                  label={`${c.name.charAt(0).toUpperCase() + c.name.slice(1)} (${c.count})`} />
              ))}
            </select>
          )}
          {facets.sizes.length > 0 && (
            <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="boutique-size-select" className={selectCls}>
              <option value="" label={ts("catalogue.filter_size")} />
              {facets.sizes.map((s) => (
                <option key={s} value={s} data-testid={`boutique-size-${slug(s)}`} label={s} />
              ))}
            </select>
          )}
          <select value={sport} onChange={(e) => setSport(e.target.value)} data-testid="boutique-sport-select" className={selectCls}>
            <option value="" label={ts("catalogue.filter_sport")} />
            {[...SPORTS].sort((a, b) => (lang === "en" ? a.en : a.fr).localeCompare(lang === "en" ? b.en : b.fr, "fr")).map((s) => (
              <option key={s.id} value={s.id} data-testid={`boutique-sport-${s.id}`}
                label={lang === "en" ? s.en : s.fr} />
            ))}
          </select>
          <select value={catNo} onChange={(e) => setCatNo(e.target.value)} data-testid="boutique-catalogue-select" className={selectCls}>
            <option value="" label={ts("catalogue.filter_catalogue")} />
            {Object.entries(
              facets.catalogues.filter((c) => c.no)
                .sort((a, b) => a.no.localeCompare(b.no, undefined, { numeric: true }))
                .reduce((acc, c) => {
                  const f = catalogueFamily(c.no);
                  (acc[f] = acc[f] || []).push(c);
                  return acc;
                }, {})
            ).map(([fam, cats]) => (
              <optgroup key={fam} label={FAMILY_NAMES[fam] || `Catalogue ${fam}.x`} data-testid={`boutique-family-${fam}`}>
                {cats.map((c) => (
                  <option key={c.no} value={c.no} data-testid={`boutique-cat-${c.no}`}
                    label={`${catalogueShortLabel(c.no)} (${c.count})`} />
                ))}
              </optgroup>
            ))}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="boutique-sort-select" className={selectCls}>
            <option value="price_asc" data-testid="boutique-sort-price-asc" label={ts("catalogue.sort_price_asc")} />
            <option value="price_desc" data-testid="boutique-sort-price-desc" label={ts("catalogue.sort_price_desc")} />
            <option value="name" data-testid="boutique-sort-name" label={ts("catalogue.sort_name")} />
          </select>
        </div>

        <p className="text-xs text-ivory/65 mb-4" data-testid="grid-count">{t("catalogue.grid_count", { n: total })}</p>

        {loading ? (
          <div className="flex items-center justify-center py-32" data-testid="grid-loading">
            <Loader2 className="w-8 h-8 animate-spin text-gold" />
          </div>
        ) : (
          <>
            {/* Grille : 1 produit par case, photo zoomable + description du catalogue */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="boutique-grid">
              {/* Plaquette sur mesure BIEN EN ÉVIDENCE en tête de boutique (demande du propriétaire)
                  — masquée si la page est désactivée (Paramètres → Pages et sections) */}
              {!isSectionOff(shopSettings, "plaquettes") && (
              <FadeUp>
                <ELink editPath="link.boutique_plaquette" to="/plaquettes-sur-mesure" data-testid="boutique-plaquette-card"
                  className="col-span-2 group relative rounded-xl overflow-hidden border-2 border-gold/60 hover:border-gold bg-ink h-full flex items-center transition-colors duration-300">
                  <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_30%_30%,#d4af37,transparent_60%)]" />
                  <div className="relative p-6 sm:p-8 flex items-center gap-5 w-full">
                    <div className="hidden sm:flex w-16 h-16 shrink-0 rounded-full border-2 border-gold items-center justify-center group-hover:bg-gold transition-colors duration-300">
                      <Ruler className="w-7 h-7 text-gold group-hover:text-ink transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gold tracking-[0.3em] uppercase text-[10px] mb-1.5"><RichText text={tx("boutique.plaq_kicker", lang === "en" ? "Made to measure" : "Sur mesure")} path="boutique.plaq_kicker" /></p>
                      <p className="font-serif text-2xl sm:text-3xl leading-tight">
                        <RichText text={tx("boutique.plaq_title", lang === "en" ? "Custom-made plaque" : "Plaquette sur mesure")} path="boutique.plaq_title" />
                      </p>
                      <p className="text-xs sm:text-sm text-ivory/60 mt-2 leading-relaxed">
                        <RichText text={tx("boutique.plaq_desc", lang === "en"
                          ? "Your size, your colour, your text, your logo — laser, diamond point or screen printing."
                          : "Vos dimensions, votre couleur, votre texte, votre logo — gravure laser, pointe diamant ou sérigraphie.")} path="boutique.plaq_desc" />
                      </p>
                      <p className="text-[10px] uppercase tracking-widest text-ivory/45 mt-2.5">
                        <RichText text={tx("boutique.plaq_materials", lang === "en" ? "Aluminum · Brass · ABS · Vinyl · Leather · Walnut" : "Aluminium · Laiton · ABS · Vinyle · Cuir · Noyer")} path="boutique.plaq_materials" />
                      </p>
                    </div>
                    <span className="shrink-0 bg-gold text-ink text-[11px] font-semibold uppercase tracking-widest rounded-full px-5 py-2.5 group-hover:bg-champagne transition-colors duration-300">
                      <RichText text={tx("boutique.plaq_cta", lang === "en" ? "Start" : "Décrire")} path="boutique.plaq_cta" />
                    </span>
                  </div>
                </ELink>
              </FadeUp>
              )}
              {items.map((p, i) => {
                const desc = lang === "en" ? (p.desc_en || p.desc) : p.desc;
                return (
                  <FadeUp key={p.id} delay={Math.min(i % 8, 6) * 0.05}>
                    <div data-testid={`grid-product-${i}`}
                      className="group bg-surface border border-ivory/10 rounded-xl overflow-hidden hover:border-gold/50 transition-colors duration-300 h-full flex flex-col">
                      <div className="relative bg-white aspect-square flex items-center justify-center p-4 cursor-pointer"
                        onClick={() => setPopupPid(p.id)}>
                        {p.image ? (
                          <img src={p.image} alt={productAlt(p, lang)} loading="lazy"
                            className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <p className="text-ink/30 text-xs">{t("product.image_pending")}</p>
                        )}
                        {p.image && (
                          <button type="button" data-testid={`grid-zoom-${i}`} title={ts("product.zoom_image")}
                            onClick={(e) => { e.stopPropagation(); setZoomImg({ src: p.image, alt: productAlt(p, lang) }); }}
                            className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-ink/80 text-gold border border-gold/40 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-gold hover:text-ink transition-all duration-300">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <button type="button" onClick={() => setPopupPid(p.id)} data-testid={`grid-open-${i}`}
                        className="p-4 text-left flex-1 flex flex-col hover:bg-gold/5 transition-colors duration-300">
                        <p className="text-sm text-ivory leading-snug line-clamp-2 min-h-[2.5em]">{lp(p, lang).name}</p>
                        {desc && (
                          <p className="text-xs text-ivory/70 leading-relaxed line-clamp-2 mt-1.5" data-testid={`grid-desc-${i}`}>{desc}</p>
                        )}
                        <div className="flex items-center justify-between mt-auto pt-3 gap-2">
                          <p className="text-gold font-serif" data-testid={`grid-price-${i}`}>
                            {t("catalogue.grid_from", { price: fmt(p.base_price) })}
                          </p>
                          <span className="text-[10px] text-ivory/65 tracking-widest uppercase">{p.sku}</span>
                        </div>
                        {p.min_qty > 1 && (
                          <p className="text-[10px] text-champagne/70 mt-1">{t("product.min_qty", { n: p.min_qty })}</p>
                        )}
                      </button>
                    </div>
                  </FadeUp>
                );
              })}
            </div>

            {items.length === 0 && (
              <p className="text-center text-ivory/70 py-16" data-testid="grid-empty">{t("catalogue.grid_empty")}</p>
            )}
            {items.length < total && (
              <div className="text-center mt-10">
                <button onClick={showMore} disabled={loadingMore} data-testid="grid-show-more"
                  className="px-8 py-3 rounded-full border border-gold/40 text-gold text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60 inline-flex items-center gap-2">
                  {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t("catalogue.grid_more", { n: total - items.length })}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {popupPid && <ProductPopup productId={popupPid} onClose={() => setPopupPid(null)} />}
      {zoomImg && (
        <ImageLightbox src={zoomImg.src} alt={zoomImg.alt} onClose={() => setZoomImg(null)} testid="grid-lightbox"
          labels={{ zoomIn: t("product.zoom_in"), zoomOut: t("product.zoom_out"), reset: t("product.zoom_reset"), close: t("product.zoom_close") }} />
      )}
    </div>
  );
}
